import { usePage, Link, router } from "@inertiajs/react";
import {
  LayoutDashboard,
  Building2,
  Server,
  GitBranch,
  AlertTriangle,
  RefreshCw,
  Settings,
  LogOut,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { toast } from "sonner";

interface User {
  id: number;
  name: string;
  email: string;
}

interface PageProps {
  current_user: User | null;
  flash: {
    notice?: string;
    alert?: string;
    success?: string;
    error?: string;
  };
}

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
}

const navItems: NavItem[] = [
  { name: "Overview", href: "/", icon: LayoutDashboard },
  { name: "Clients", href: "/clients", icon: Building2 },
  { name: "n8n Instances", href: "/n8n_instances", icon: Server },
  { name: "Workflows", href: "/workflows", icon: GitBranch },
  { name: "Errors", href: "/errors", icon: AlertTriangle },
  { name: "Sync Health", href: "/sync_health", icon: RefreshCw },
  { name: "Settings", href: "/settings", icon: Settings },
];

function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const { url } = usePage();
  const isActive =
    url === item.href || (item.href !== "/" && url.startsWith(item.href));

  return (
    <Link
      href={item.href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
        isActive
          ? "bg-sidebar-primary text-sidebar-primary-foreground"
          : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
      )}
    >
      <item.icon className="h-4 w-4" />
      {item.name}
    </Link>
  );
}

function SideNav() {
  const { props } = usePage<PageProps>();
  const user = props.current_user;

  const handleLogout = () => {
    router.delete("/logout");
  };

  return (
    <div className="flex h-screen flex-col text-sidebar-foreground">
      {/* Header - fixed */}
      <div className="flex h-14 shrink-0 items-center border-b border-sidebar-border px-4">
        <Link
          href="/"
          className="flex items-center gap-2 font-semibold text-sidebar-foreground"
        >
          <Server className="h-6 w-6 text-sidebar-primary" />
          <span>Fleet</span>
        </Link>
      </div>

      {/* Navigation - scrollable if needed */}
      <div className="flex-1 overflow-y-auto py-2">
        <nav className="grid gap-1 px-2">
          {navItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </nav>
      </div>

      {/* User section - fixed at bottom */}
      <div className="shrink-0 border-t border-sidebar-border p-4">
        {user && (
          <div className="mb-3">
            <p className="text-sm font-medium text-sidebar-foreground">
              {user.name}
            </p>
            <p className="text-xs text-sidebar-foreground/60">{user.email}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </Button>
      </div>
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { props } = usePage<PageProps>();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (props.flash.notice || props.flash.success) {
      toast.success(props.flash.notice || props.flash.success);
    }
    if (props.flash.alert || props.flash.error) {
      toast.error(props.flash.alert || props.flash.error);
    }
  }, [props.flash]);

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar */}
      <aside className="hidden w-64 shrink-0 border-r bg-sidebar md:block">
        <SideNav />
      </aside>

      {/* Mobile Header */}
      <div className="flex flex-1 flex-col">
        <header className="flex h-14 items-center gap-4 border-b bg-background px-4 md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SideNav />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2 font-semibold">
            <Server className="h-5 w-5" />
            <span>Fleet</span>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>

      <Toaster />
    </div>
  );
}
